import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { ChevronDownIcon, Globe } from 'lucide-react';

export const LanguageSelector = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentLang, setCurrentLang] = useState(i18n.language);

  const languages = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  // Route mapping for language switching
  const routeMapping = {
    '/fr': '/es',
    '/es': '/fr', 
    '/fr/outils': '/es/herramientas',
    '/es/herramientas': '/fr/outils',
    '/fr/comparateur-electricite': '/es/luz',
    '/es/luz': '/fr/comparateur-electricite',
    '/fr/modeles-emails': '/es/modelos-email',
    '/es/modelos-email': '/fr/modeles-emails',
    '/fr/demenagement': '/es/mudanza',
    '/es/mudanza': '/fr/demenagement',
    '/fr/simulateur-solaire': '/es/simulador-solar',
    '/es/simulador-solar': '/fr/simulateur-solaire',
    '/fr/suivi-depenses': '/es/seguimiento-gastos',
    '/es/seguimiento-gastos': '/fr/suivi-depenses',
    '/fr/tracker-prix': '/es/seguimiento-precios',
    '/es/seguimiento-precios': '/fr/tracker-prix',
    '/fr/comparateur-internet': '/es/comparador-internet',
    '/es/comparador-internet': '/fr/comparateur-internet',
    '/fr/inflation': '/es/inflacion',
    '/es/inflacion': '/fr/inflation',
    '/fr/a-propos': '/es/acerca-de',
    '/es/acerca-de': '/fr/a-propos',
    '/fr/contact': '/es/contacto',
    '/es/contacto': '/fr/contact',
    '/fr/confidentialite': '/es/privacidad',
    '/es/privacidad': '/fr/confidentialite'
  };

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setCurrentLang(langCode);
    
    // Navigate to equivalent page in new language
    const currentPath = location.pathname;
    const newPath = routeMapping[currentPath];
    
    if (newPath) {
      navigate(newPath);
    } else {
      // Default to home in new language
      navigate(langCode === 'es' ? '/es' : '/fr');
    }
  };

  const currentLanguage = languages.find(lang => lang.code === currentLang);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2 bg-white border-2 border-sage-600 text-sage-800 hover:bg-sage-50 transition-all duration-200 px-4 py-2 font-medium"
          data-testid="language-selector-trigger"
        >
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{currentLanguage?.flag} {currentLanguage?.name}</span>
          <span className="sm:hidden text-lg">{currentLanguage?.flag}</span>
          <ChevronDownIcon className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[150px] bg-white border border-gray-200 shadow-lg z-50">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => changeLanguage(language.code)}
            className="flex items-center gap-3 cursor-pointer hover:bg-sage-50 transition-colors py-2 px-3"
            data-testid={`language-option-${language.code}`}
          >
            <span className="text-lg">{language.flag}</span>
            <span className="font-medium text-sage-800">{language.name}</span>
            {currentLang === language.code && (
              <div className="w-2 h-2 bg-sage-600 rounded-full ml-auto" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};